import * as React from 'react';
import { useGetModuleNames, useHasModule } from 'api/modules';
import { useTranslations } from 'i18n';
import { usePermissions, UserPermissionsEnum } from 'auth';
import { useHasFeature } from 'api';
import { FrontendConfigFeaturesEnum } from 'oas';

interface SettingsMenuItem {
    id?: string;
    label?: string;
    element?: React.ReactElement;
    isShown: boolean;
    hasDivider?: boolean;
}

export const useSettingsMenuItems = (): SettingsMenuItem[] => {
    const { data: modules } = useGetModuleNames();
    const [menuItems, setMenuItems] = React.useState<SettingsMenuItem[]>([]);
    const { __ } = useTranslations();
    const permissions = usePermissions();
    const hasMyAccount = useHasFeature(FrontendConfigFeaturesEnum.MyAccountReactJs);
    const showLimitedMenu = useHasFeature(FrontendConfigFeaturesEnum.LimitedSettings);
    // Temporary hide the menu while development
    const hasConnectionsModule = useHasModule('Connections');

    React.useEffect(() => {
        if (!modules) {
            return;
        }

        const AthletesSettings = React.lazy(() => import('./pages/athletes-settings'));
        const ApiTokensSettings = React.lazy(() => import('./pages/api-tokens-settings'));
        const LegacySettings = React.lazy(() => import('./pages/legacy-settings'));
        const ActivityAdminSettings = React.lazy(() => import('./pages/activity-admin-settings'));
        const AthleteThresholdSettings = React.lazy(() => import('./pages/athlete-threshold-settings'));
        const WebhookSettings = React.lazy(() => import('./pages/webhook-settings'));
        const ConnectionsSettings = React.lazy(() => import('./pages/connections-settings'));

        const newMenuItems = [
            {
                id: 'athletes',
                label: __('settings.athletes'),
                element: <AthletesSettings />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsAthletes),
            },
            {
                id: 'thresholds',
                label: __('settings_thresholds.athlete_thresholds'),
                element: <AthleteThresholdSettings />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsThresholds),
            },
            {
                id: 'activities',
                label: __('misc.activity_admin'),
                element: <ActivityAdminSettings />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsActivities),
            },
            {
                id: 'api_tokens',
                label: __('settings_api_token.api_tokens'),
                element: <ApiTokensSettings />,
                isShown: modules.includes('APITokenAdmin') && permissions.includes(UserPermissionsEnum.SettingsTokens),
            },
            {
                id: 'averages',
                label: __('settings.averages'),
                element: <LegacySettings url="averages" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsAverages),
            },
            {
                id: 'bands',
                label: __('settings.bands'),
                element: <LegacySettings url="bands" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsBands),
            },
            {
                id: 'connections',
                label: __('settings.connections'),
                element: <ConnectionsSettings />,
                isShown: !!hasConnectionsModule,
            },
            {
                id: 'parameters',
                label: __('settings.parameters'),
                element: <LegacySettings url="parameters" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsParameters),
            },
            {
                id: 'period_admin',
                label: __('misc.period_admin'),
                element: <LegacySettings url="period_admin" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsPeriods),
            },
            {
                id: 'positions',
                label: __('settings.positions'),
                element: <LegacySettings url="positions" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsPositions),
            },
            {
                id: 'rawfiles',
                label: __('settings.rawfiles'),
                element: <LegacySettings url="rawfiles" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsRawfiles),
            },
            {
                id: 'sd_export',
                label: __('settings.manage_sd'),
                element: <LegacySettings url="sd_export" />,
                isShown: modules.includes('ExportReplay') && permissions.includes(UserPermissionsEnum.SettingsSdexport),
            },
            {
                id: 'smartabase',
                label: 'Smartabase',
                element: <LegacySettings url="smartabase" />,
                isShown: modules.includes('Smartabase') && permissions.includes(UserPermissionsEnum.SettingsSmartabase),
            },
            {
                id: 'tags',
                label: __('settings.tags'),
                element: <LegacySettings url="tags" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsTags),
            },
            {
                id: 'teams',
                label: __('settings.teams'),
                element: <LegacySettings url="teams" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsTeams),
            },
            {
                id: 'users',
                label: __('settings.users'),
                element: <LegacySettings url="users" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsUsers),
            },
            {
                id: 'venues',
                label: __('settings.venues'),
                element: <LegacySettings url="venues" />,
                isShown: permissions.includes(UserPermissionsEnum.SettingsVenues),
            },
            {
                id: 'webhooks',
                label: __('misc.webhooks'),
                element: <WebhookSettings />,
                isShown: modules.includes('Webhooks') && permissions.includes(UserPermissionsEnum.SettingsWebhook),
            },
            {
                id: 'myaccount',
                label: __('settings.account'),
                element: <LegacySettings url="myaccount" />,
                isShown: !hasMyAccount,
                hasDivider: true,
            },
        ];

        let filteredMenuItems = newMenuItems.filter((item) => item.isShown);

        if (showLimitedMenu) {
            filteredMenuItems = filteredMenuItems.filter(
                (item) =>
                    !(
                        item.id === 'averages' ||
                        item.id === 'thresholds' ||
                        item.id === 'parameters' ||
                        item.id === 'period_admin' ||
                        item.id === 'tags' ||
                        item.id === 'users' ||
                        item.id === 'api_tokens' ||
                        item.id === 'webhooks' ||
                        item.id === 'venues'
                    ),
            );
        }

        setMenuItems(filteredMenuItems);
    }, [modules, hasConnectionsModule, permissions, hasMyAccount, __, showLimitedMenu]);

    return menuItems;
};
